import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  CircularProgress,
  Stack,
  Divider,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';

import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Typography } from 'src/components/Typography';
import { ApplicationModalProps } from './CreateJobForm.types';
import { calcAge } from 'src/utils/calcAge';
import { BUTTON_COLOR, BUTTON_VARIANT } from 'src/components/Button/types';
import { TYPOGRAPHY_COLORS, TYPOGRAPHY_WEIGHT } from 'src/components/Typography/types';
import { Avatar } from 'src/components/Avatar';
import {
  AVATAR_BG_COLOR,
  AVATAR_SHADOW,
  AVATAR_SIZE,
  AVATAR_VARIANT,
} from 'src/components/Avatar/types';

export const ApplicationModal: React.FC<ApplicationModalProps> = ({
  isOpen,
  handleClose,
  worker,
  hashAttributes,
  handleOpenWorkerProfile,
  loading,
  handleAcceptApplication,
  handleDeclineApplication,
  applicationId,
}) => {
  const { t } = useTranslation();

  const workerAge = useMemo(() => {
    return worker?.birthDay ? calcAge(new Date(worker.birthDay)) : null;
  }, [worker?.birthDay]);

  const lastImageUrl =
    worker.imageUrls && worker.imageUrls.length > 0
      ? worker.imageUrls[worker.imageUrls.length - 1]
      : undefined;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ px: 5, py: 1 }}>
        <Stack spacing={2}>
          <DialogTitle
            sx={{ px: 0, pb: 1, borderBottom: '1px solid black', borderColor: 'divider', mb: 2 }}>
            <Typography variant="h5" fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}>
              {worker.name} {workerAge && `(${workerAge})`}
            </Typography>
          </DialogTitle>

          {worker.interviewId ? (
            <Box sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <iframe
                title={t('workerProfile.interviewVideoTitle', { name: worker.name })}
                style={{ border: 'none' }}
                width="100%"
                height="315"
                allowFullScreen
                src={`https://embed.myinterview.com/player.v3.html?apiKey=xkqnejs448zhbgzqnuqljpm5&hashTimestamp=${hashAttributes?.timestamp}&hash=${hashAttributes?.hash}&video=${worker.interviewId}&fs=0`}
              />
            </Box>
          ) : (
            <Box display="flex" justifyContent="center">
              <Avatar
                alt={worker.name}
                bgColor={AVATAR_BG_COLOR.DARK}
                src={lastImageUrl}
                variant={AVATAR_VARIANT.ROUNDED}
                size={AVATAR_SIZE.XXL}
                shadow={AVATAR_SHADOW.SM}
              />
            </Box>
          )}

          <Box display="flex" justifyContent="center">
            <Button
              variant={BUTTON_VARIANT.GRADIENT}
              color={BUTTON_COLOR.INFO}
              onClick={() => {
                handleClose();
                handleOpenWorkerProfile(worker);
              }}>
              {t('workerCard.profile')}
            </Button>
          </Box>

          <Divider />

          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}>
                <Trans>profileScreen.userEmail</Trans>
              </Typography>
              <Typography
                variant="body2"
                fontWeight={TYPOGRAPHY_WEIGHT.REGULAR}
                color={TYPOGRAPHY_COLORS.INFO}
                sx={{ lineHeight: 1.5 }}>
                {worker.email || t('jobPage.createJobModal.notProvided')}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}>
                <Trans>profileScreen.userPhoneNumber</Trans>
              </Typography>
              <Typography
                variant="body2"
                fontWeight={TYPOGRAPHY_WEIGHT.REGULAR}
                color={TYPOGRAPHY_COLORS.INFO}
                sx={{ lineHeight: 1.5 }}>
                {worker.phoneNumber || t('jobPage.createJobModal.notProvided')}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}>
                <Trans>profileScreen.userNationality</Trans>
              </Typography>
              <Typography
                variant="body2"
                fontWeight={TYPOGRAPHY_WEIGHT.REGULAR}
                color={TYPOGRAPHY_COLORS.INFO}
                sx={{ lineHeight: 1.5 }}>
                {worker.nationality || t('jobPage.createJobModal.notProvided')}
              </Typography>
            </Box>
          </Stack>

          <Divider />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              pt: 1,
            }}>
            {loading ? (
              <Box sx={{ mx: 'auto' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Button
                  variant={BUTTON_VARIANT.CONTAINED}
                  color={BUTTON_COLOR.ERROR}
                  onClick={() => handleDeclineApplication(applicationId)}
                  fullWidth>
                  <Trans>generic.decline</Trans>
                </Button>
                <Button
                  variant={BUTTON_VARIANT.CONTAINED}
                  color={BUTTON_COLOR.SUCCESS}
                  onClick={() => handleAcceptApplication(applicationId)}
                  fullWidth>
                  <Trans>generic.accept</Trans>
                </Button>
              </>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          <Trans>generic.cancel</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  );
};
